import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Crown, Target, MessageSquare, TrendingUp, Users, Globe, ArrowRight, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { mockData } from '../mockData';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const ExclusiveExecutivePage = () => {
  const benefits = [
    "Shape up or improve your personal image and that of your organization",
    "Enhance and boost up your communications and communication skills",
    "Strategic guidance from highly experienced coaches and mentors",
    "Relevant connections and relationships anywhere in the world",
    "Full support from strategizing point to full achievements",
    "Personalized approach to ensure success in your endeavors"
  ];

  const targetAudience = [
    {
      icon: TrendingUp,
      title: "Major Entrepreneurship",
      description: "Building and scaling successful businesses"
    },
    {
      icon: Crown,
      title: "Top Political Leadership",
      description: "Navigating political landscapes effectively"
    },
    {
      icon: Users,
      title: "Entertainment Careers",
      description: "Top music careers and public figures"
    },
    {
      icon: Globe,
      title: "Major Public Careers",
      description: "High-profile positions and influence"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero */}
      <motion.section 
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp} className="text-white space-y-8">
              <Badge className="bg-white/20 text-white border-0 px-6 py-2 text-sm">
                <Crown className="w-4 h-4 mr-2" />
                Premium Service
              </Badge>
              <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-bold leading-tight">
                EXCLUSIVE AND EXECUTIVE
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-2xl text-blue-100 leading-relaxed">
                Are you on your way for a major entrepreneurship, a top political leadership, a top music career or any major public career?
              </motion.p>
              <motion.p variants={fadeInUp} className="text-xl text-blue-200">
                <strong>OL Consult</strong> is the missing key to your full success.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Link to="/contact">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-10 py-7 shadow-xl group">
                      Schedule Consultation
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-blue-700/20 rounded-3xl blur-3xl"></div>
              <img
                src={mockData.companyInfo.logo}
                alt="OL-CONSULT Premium"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover border-4 border-white"
              />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* What We Offer */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
        className="py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-900 px-6 py-2 mb-4">What We Offer</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Premium Executive Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're looking to shape up your personal image or need support to achieve your dreams, our team of highly experienced coaches and mentors is ready to take you to a whole different level.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <motion.div whileHover={{ scale: 1.02, y: -5 }} transition={{ duration: 0.2 }}>
                  <Card className="border-2 hover:border-blue-500 hover:shadow-lg transition-all h-full">
                    <CardContent className="p-6 flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-lg text-gray-700 leading-relaxed">{benefit}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Target Audience */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Who Is This For?</h2>
            <p className="text-xl text-gray-600">Executive services tailored for ambitious professionals</p>
          </motion.div>

          <motion.div variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {targetAudience.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div key={index} variants={fadeInUp}>
                  <motion.div 
                    whileHover={{ scale: 1.05, y: -10 }} 
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="text-center border-2 hover:border-blue-500 hover:shadow-xl transition-all group h-full">
                      <CardContent className="p-8">
                        <div className="bg-blue-100 p-5 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                          <IconComponent className="w-10 h-10 text-blue-900" />
                        </div>
                        <h3 className="text-xl font-bold text-blue-900 mb-3">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </motion.section>

      {/* How We Work */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
        className="py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Approach</h2>
            <p className="text-xl text-gray-600">Comprehensive support throughout your journey</p>
          </motion.div>

          <motion.div variants={staggerContainer} className="space-y-8">
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-900 text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 text-xl font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-3">Strategic Planning</h3>
                    <p className="text-gray-700 leading-relaxed">
                      From the strategizing point to the full achievements, we will be on top of it all.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-900 text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 text-xl font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-3">Continuous Support</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We will walk and work with you throughout key steps in your journey to success.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-900 text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 text-xl font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-3">Global Connections</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We will provide you with relevant connections and relationships anywhere in the world, based on your needs to ensure success in your endeavors.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInUp}
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-900 to-blue-800 text-white"
      >
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold">
            Ready to Reach Your Full Potential?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-2xl text-blue-100">
            Our team of highly experienced coaches and mentors is ready to step in and take you to a whole different level.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-12 py-7 shadow-xl group">
                  Schedule Your Executive Consultation
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default ExclusiveExecutivePage;
